import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, NeuralChatApiError } from "../api/client";
import { useNeuralChatAuth } from "../contexts/AuthContext";
import { Message, RoomMember } from "../types";
import { getInitials } from "../utils/routes";
import {
  Avatar, ComposerForm, ComposerHint, ComposerToolbar, ComposerWrap, MentionMenu,
  MentionOption, ReplyPreview, Select, SendButton, TaskField, TaskFields, Textarea, TinyButton, MutedText,
} from "../styles";

type Props = { roomId: string; roomGeneration: number; members: RoomMember[]; archived: boolean; replyTo: Message | null; onCancelReply: () => void; onSent: (message: Message, requestRoomId: string, requestGeneration: number) => void; onTaskRequestCreated?: () => void };

const mentionToken = (value: string, caret: number) => {
  const before = value.slice(0, caret);
  const match = before.match(/(^|\s)@([^\s@]*)$/);
  return match ? match[2].toLowerCase() : null;
};

const canonical = (body: string, type: string, replyTo: string | null, ids: string[]) => JSON.stringify({ body, type, replyTo, ids: Array.from(new Set(ids)).sort() });

export const MessageComposer: React.FC<Props> = ({ roomId, roomGeneration, members, archived, replyTo, onCancelReply, onSent, onTaskRequestCreated }) => {
  const auth = useNeuralChatAuth();
  const [body, setBody] = useState("");
  const [messageType, setMessageType] = useState<"DISCUSSION" | "TASK_REQUEST">("DISCUSSION");
  const [requestType, setRequestType] = useState<"RESEARCH" | "PLANNING" | "DEVELOPMENT" | "DESIGN" | "INTERNAL_REVIEW">("RESEARCH");
  const [targetAgentId, setTargetAgentId] = useState("");
  const [taskTitle, setTaskTitle] = useState("");
  const [expectedOutput, setExpectedOutput] = useState("");
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [caret, setCaret] = useState(0);
  const [mentionIndex, setMentionIndex] = useState(0);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [clientId, setClientId] = useState(() => api.newClientMessageId());
  const meaningRef = useRef("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const sendAbortRef = useRef<AbortController | null>(null);
  const sendSequenceRef = useRef(0);
  const activeMembers = useMemo(() => members.filter((member) => member.is_active), [members]);
  const activeAgents = useMemo(() => activeMembers.filter((member) => member.kind === "AGENT"), [activeMembers]);
  const isTaskRequest = messageType === "TASK_REQUEST";
  const query = mentionToken(body, caret);
  const mentionCandidates = query === null ? [] : activeMembers.filter((member) => member.display_name.toLowerCase().includes(query) || member.slug.toLowerCase().includes(query));

  useEffect(() => {
    setMentionIds((current) => current.filter((id) => activeMembers.some((member) => member.id === id && body.includes(`@${member.display_name}`))));
  }, [activeMembers, body]);

  useEffect(() => {
    if (!activeAgents.some((member) => member.id === targetAgentId)) {
      setTargetAgentId(activeAgents[0]?.id || "");
    }
  }, [activeAgents, targetAgentId]);

  useEffect(() => {
    sendSequenceRef.current += 1;
    sendAbortRef.current?.abort();
    sendAbortRef.current = null;
    setSending(false);
    setTargetAgentId("");
    setRequestType("RESEARCH");
    setTaskTitle("");
    setExpectedOutput("");
  }, [roomId, roomGeneration]);

  useEffect(() => () => {
    sendSequenceRef.current += 1;
    sendAbortRef.current?.abort();
  }, []);

  const selectMention = (member: RoomMember) => {
    const before = body.slice(0, caret);
    const after = body.slice(caret);
    const replaced = before.replace(/(^|\s)@([^\s@]*)$/, `$1@${member.display_name} `) + after;
    setBody(replaced);
    setMentionIds((current) => Array.from(new Set([...current, member.id])));
    setMentionIndex(0);
    requestAnimationFrame(() => {
      const position = before.replace(/(^|\s)@([^\s@]*)$/, `$1@${member.display_name} `).length;
      textareaRef.current?.focus();
      textareaRef.current?.setSelectionRange(position, position);
      setCaret(position);
    });
  };

  const submit = async () => {
    const trimmed = body.trim();
    if (sending || !trimmed || archived) return;
    const ids = Array.from(new Set(mentionIds));
    if (isTaskRequest && (!targetAgentId || !taskTitle.trim() || !expectedOutput.trim())) {
      setError("담당 에이전트, 제목, 기대 결과를 입력해 주세요.");
      return;
    }
    const signature = canonical(
      trimmed,
      messageType,
      replyTo?.id || null,
      isTaskRequest
        ? [targetAgentId, requestType, taskTitle.trim(), expectedOutput.trim()]
        : ids,
    );
    let requestClientId = clientId;
    if (meaningRef.current && meaningRef.current !== signature) {
      requestClientId = api.newClientMessageId();
      setClientId(requestClientId);
    }
    meaningRef.current = signature;
    const requestRoomId = roomId;
    const requestGeneration = roomGeneration;
    const requestSequence = sendSequenceRef.current + 1;
    sendSequenceRef.current = requestSequence;
    const controller = new AbortController();
    sendAbortRef.current = controller;
    setSending(true); setError("");
    try {
      const result = isTaskRequest
        ? await api.createTaskRequest({
          client_request_id: requestClientId,
          request_type: requestType,
          target_member_id: targetAgentId,
          title: taskTitle.trim(),
          instruction: trimmed,
          expected_output: expectedOutput.trim(),
        }, requestRoomId, controller.signal)
        : await api.postMessage({ client_message_id: requestClientId, message_type: messageType, body: trimmed, reply_to: replyTo?.id || null, mention_member_ids: ids }, requestRoomId, controller.signal);
      if (controller.signal.aborted || requestSequence !== sendSequenceRef.current) return;
      onSent(result.message, requestRoomId, requestGeneration);
      if (isTaskRequest) onTaskRequestCreated?.();
      setBody(""); setMentionIds([]); setMessageType("DISCUSSION"); setRequestType("RESEARCH"); setTargetAgentId(""); setTaskTitle(""); setExpectedOutput(""); setClientId(api.newClientMessageId()); meaningRef.current = ""; onCancelReply();
    } catch (reason) {
      if (controller.signal.aborted || requestSequence !== sendSequenceRef.current) return;
      if (reason instanceof NeuralChatApiError && reason.status === 401) auth.markUnauthenticated();
      else if (reason instanceof NeuralChatApiError && reason.code === "NEURALCHAT_IDEMPOTENCY_CONFLICT") setError("같은 요청 ID에 다른 내용이 사용되었습니다. 내용을 확인하고 다시 시도해 주세요.");
      else setError(reason instanceof NeuralChatApiError ? reason.message : "메시지를 보내지 못했습니다. 다시 시도해 주세요.");
    } finally {
      if (requestSequence === sendSequenceRef.current) {
        sendAbortRef.current = null;
        setSending(false);
      }
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (mentionCandidates.length) {
      if (event.key === "ArrowDown") { event.preventDefault(); setMentionIndex((index) => (index + 1) % mentionCandidates.length); return; }
      if (event.key === "ArrowUp") { event.preventDefault(); setMentionIndex((index) => (index - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
      if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); selectMention(mentionCandidates[mentionIndex]); return; }
      if (event.key === "Escape") { setCaret(-1); return; }
    }
    if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); void submit(); }
  };

  if (archived) return <ComposerWrap><MutedText>보관된 채널에서는 새 메시지를 작성할 수 없습니다.</MutedText></ComposerWrap>;
  return (
    <ComposerWrap>
      {replyTo && <ReplyPreview><span>답장 대상: {replyTo.sender.display_name} · {replyTo.body.slice(0, 100)}</span><TinyButton type="button" onClick={onCancelReply}>취소</TinyButton></ReplyPreview>}
      <ComposerToolbar><Select value={messageType} onChange={(event) => setMessageType(event.target.value as "DISCUSSION" | "TASK_REQUEST")} aria-label="메시지 종류"><option value="DISCUSSION">Discussion</option><option value="TASK_REQUEST">구조화 TaskRequest</option></Select><ComposerHint>{isTaskRequest ? "실행되지 않는 시뮬레이션 요청" : "Enter 전송 · Shift + Enter 줄바꿈 · @ 멘션"}</ComposerHint></ComposerToolbar>
      {isTaskRequest && <TaskFields>
        <Select value={requestType} onChange={(event) => setRequestType(event.target.value as typeof requestType)} aria-label="요청 유형">
          <option value="RESEARCH">Research</option><option value="PLANNING">Planning</option><option value="DEVELOPMENT">Development</option><option value="DESIGN">Design</option><option value="INTERNAL_REVIEW">Internal review</option>
        </Select>
        <Select value={targetAgentId} onChange={(event) => setTargetAgentId(event.target.value)} aria-label="담당 에이전트">
          <option value="">담당 에이전트 선택</option>
          {activeAgents.map((member) => <option key={member.id} value={member.id}>{member.display_name} · @{member.slug}</option>)}
        </Select>
        <TaskField value={taskTitle} onChange={(event) => setTaskTitle(event.target.value)} maxLength={200} placeholder="요청 제목" aria-label="요청 제목" />
        <TaskField value={expectedOutput} onChange={(event) => setExpectedOutput(event.target.value)} maxLength={10000} placeholder="기대 결과" aria-label="기대 결과" />
      </TaskFields>}
      <ComposerForm onSubmit={(event) => { event.preventDefault(); void submit(); }}>
        <Textarea ref={textareaRef} value={body} maxLength={10000} onChange={(event) => { setBody(event.target.value); setCaret(event.target.selectionStart); }} onClick={(event) => setCaret(event.currentTarget.selectionStart)} onKeyUp={(event) => setCaret(event.currentTarget.selectionStart)} onKeyDown={onKeyDown} placeholder="팀에 메시지를 남겨 보세요" aria-label="메시지" disabled={sending} />
        <SendButton type="submit" disabled={sending || !body.trim()}>{sending ? "전송 중…" : "전송"}</SendButton>
      </ComposerForm>
      {error && <MutedText role="alert">{error}</MutedText>}
      {!isTaskRequest && mentionCandidates.length > 0 && <MentionMenu role="listbox" aria-label="멘션 대상">{mentionCandidates.map((member, index) => <MentionOption key={member.id} type="button" role="option" aria-selected={index === mentionIndex} $active={index === mentionIndex} onMouseDown={(event) => event.preventDefault()} onClick={() => selectMention(member)}><Avatar $kind={member.kind} $small>{getInitials(member.display_name)}</Avatar><span>{member.display_name} <small>@{member.slug}</small></span></MentionOption>)}</MentionMenu>}
    </ComposerWrap>
  );
};
